import { Config, LegacyKeyStrings } from './constants'
import { Language, LocaleNames } from './language'
import { mockApi } from './MockApi/mockApi'
import { Program, Workshop } from './Models/workshops'
import { WorkshopMenuItem } from './Models/workshopMenu'
import { LegacyJsonResponseSuccess, Dictionary, DropdownData, LegacyJsonResponse, SessionInfo } from './Models/system'
import { prepWorkshopData, prepDropdownData, prepMenuItems, prepParticipantData, prepRegisterPayload } from './Models/logic'
import { Participant, ParticipantDeregisterData, ParticipantRegisterData, ParticipantWorkbooks, RelatedParticipant } from './Models/participant'
import { appendUrlPath } from './utility'

export const URLS = {
	// HELP_PAGE: 'https://www.ontariosoilcrop.org/oscia-programs/workshops-webinars', 
	LEADERS_LIST: 'https://www.ontariosoilcrop.org/association/contact-us/oscia-field-staff',
	WORKSHOPS_LOGOUT: '/public/logout', 
	EXTERNAL_EEFP: 'https://www.electronicefp.com', 

	/*
	OPNET_LOGIN: Config.OntarioProgramsUrl, 
	OPNET_DASHBOARD: Config.OntarioProgramsUrl + 'AccountDashboard.aspx', 
	*/

	IDENTITY_SERVER_DASHBOARD: Config.IdentityServerUrl, 

	IDENTITY_SERVER_LOGIN_AND_RETURN: appendUrlPath(Config.IdentityServerUrl || '', Config.IdentityServerLoginAndReturnPath!), 

	// Now handled in controller action via redirect
	// OPNET_LOGOUT: Config.OntarioProgramsUrl + 'logout', 
}


export interface ApiDictionary<T> {
	// WorkshopExtController.cs => WorkshopController.GetWorkshopDataAsync()
	'/workshop/registration/list': T

	// WorkshopExtController.cs => WorkshopController.ActiveWorkshops()
	'/workshop/registration/activeworkshops': T

	'/workshop/getregistrationmenu': T

	'/collections/getdictionaries': T

	// WorkshopController.cs:3164 => WorkshopController.ParticipantGetAsync()
	'/workshop/participant/get': T

	// WorkshopControllerParticipants.cs:333 => WorkshopController.ParticipantSave()
	'/workshop/participant/save': T 

	// WorkshopController.cs:3294 => WorkshopController.ParticipantDelete()
	'/workshop/participant/delete': T

	// '/public/logout': T

	// Default view page, used as api to set langauge preference
	// WorkshopExtController.cs => WorkshopController.Registration()
	'/workshop/registration': T

	'/public/sessionupdate': T
	'/public/renewsession': T
}

export type ApiUrl = keyof ApiDictionary<any>

function getFullPathIfNecessary(path: string): string {
	return Config.Server + path
}

export function get<T>(url: ApiUrl, data?: any): Promise<T> {
	if (Config.MockApi) {
		return new Promise((resolve, reject) => {
			setTimeout(() => {
				resolve(JSON.parse(mockApi[url]))
			}, Config.MockApiLatency)
		})
	}

	else {
		let queryString = ''

		if (data) {
			queryString = '?' + new URLSearchParams(data).toString()
		}

		return fetch(getFullPathIfNecessary(url) + queryString)
			.then(response => {
				return response.json()
			})
	}
}

export function post<T>(url: ApiUrl, data?: any): Promise<T> {
	if (Config.MockApi) {
		return new Promise((resolve, reject) => {
			setTimeout(() => {
				resolve(JSON.parse(mockApi[url]))
			}, Config.MockApiLatency)
		})
	}
	else {
		const init: RequestInit = {
			method: 'POST'
		}

		if (data) {
			init.body = JSON.stringify(data)

			init.headers = {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		}

		return fetch(getFullPathIfNecessary(url), init)
			.then(response => {
				return response.json()
			})
	}
}

export interface QueryStringWorkshopParameters {
	workshopId?: number
	workshopEventId?: number
}

export function getWorkshopParametersFromQueryString(workshops: Array<Workshop>): QueryStringWorkshopParameters {
	let result: QueryStringWorkshopParameters = {}

	const queryParams = new URLSearchParams(window.location.search)

	const workshopEventIdString = queryParams.get('workshopEventId') || ''
	const workshopEventId = Number.parseInt(workshopEventIdString)

	if (workshopEventId) {
		const workshop = workshops.find(ws => ws.Events.some(e => e.EventId == workshopEventId))

		if (workshop) {
			result = {
				workshopEventId,
				workshopId: workshop.WorkshopId
			}
		}
	}

	return result
}

export const API = {
	getWorkshopData(): Promise<Array<Workshop>> {
		return get<LegacyJsonResponseSuccess<Array<Program>>>('/workshop/registration/list')
			.then(prepWorkshopData)
	},
	getActiveWorkshops(): Promise<Array<Workshop>> {
		return get<LegacyJsonResponseSuccess<Array<Workshop>>>('/workshop/registration/activeworkshops')
			.then(r => r.Data)
	},
	getWorkshopMenu(): Promise<Array<WorkshopMenuItem>> {
		return get<LegacyJsonResponseSuccess<Array<WorkshopMenuItem>>>('/workshop/getregistrationmenu')
			.then(prepMenuItems)
	},
	getDropdownData(language: Language): Promise<DropdownData> {
		return post<LegacyJsonResponseSuccess<Array<Dictionary>>>('/collections/getdictionaries', {
			names: Object.entries(LegacyKeyStrings).map(([key, value]) => value).join(','), 
			languageCode: LocaleNames[language]
		})
		.then(dictionaries => prepDropdownData(dictionaries, language))
	}, 
	getParticipant(publicUserId: number): Promise<Participant> {
		return post<LegacyJsonResponseSuccess<ParticipantWorkbooks>>('/workshop/participant/get', { publicUserId })
		.then(prepParticipantData)
	}, 
	registerParticipant(participant: Participant, additionalParticipant: RelatedParticipant | undefined, workshopEventId: number,): Promise<LegacyJsonResponse<ParticipantRegisterData>> {
		return post<LegacyJsonResponse<ParticipantRegisterData>>('/workshop/participant/save', prepRegisterPayload(workshopEventId, participant, additionalParticipant))
	},
	deregisterParticipant(workshopEventId: number): Promise<LegacyJsonResponse<ParticipantDeregisterData>> {
		return post<LegacyJsonResponse<ParticipantRegisterData>>('/workshop/participant/delete', { workshopEventId })
	}, 

	/*
	logout(): Promise<undefined> {
		return get<undefined>('/public/logout')
	}, 
	*/

	// TODO: set this up so that we can more cleanly deal with html text response (parsing json breaks here)
	// Probably should move setting language preference into a true json api at some point
	setPreferredLanguage(language: Language) {
		const languageCode = LocaleNames[language]
		return get('/workshop/registration', { languageCode })
			.catch(e => {
				// we expect parsing error here since the api isn't equipped to parse a non-json response at this time
				if (!Config.MockApi && !e.message.startsWith('Unexpected token < in JSON at position 0')) throw e
			})
	}, 
	checkSession(): Promise<SessionInfo> {
		return post('/public/sessionupdate')
	}, 

	// TODO: figure out how to better mock text responses 
	// (that don't fake serialize as expected)
	extendSession(): Promise<'ok'> {
		return post('/public/renewsession')
	}
}